import { HTML_ENTITIES, REGEX_PATTERNS } from '../constants';
import { TreeNode } from '../types';

import { BaseParser } from './BaseParser';

interface ListItem {
  label: string;
  nestedContent?: string;
}

export class HTMLParser extends BaseParser {
  readonly name = 'HTMLParser';

  canParse(input: string): boolean {
    return REGEX_PATTERNS.htmlList.test(input);
  }

  protected parseInternal(input: string): TreeNode[] {
    return this.parseListItems(input);
  }

  private parseListItems(html: string): TreeNode[] {
    const result: TreeNode[] = [];
    let currentIndex = 0;

    while (currentIndex < html.length) {
      const listItemData = this.extractNextListItem(html, currentIndex);
      if (!listItemData) break;

      const { item, nextIndex } = listItemData;

      if (item.label) {
        const node = this.createNode(item.label);

        if (item.nestedContent) {
          const nestedNodes = this.parseListItems(item.nestedContent);
          node.nodes.push(...nestedNodes);
        }

        result.push(node);
      }

      currentIndex = nextIndex;
    }

    return result;
  }

  private extractNextListItem(html: string, startIndex: number): { item: ListItem; nextIndex: number } | null {
    const liStart = html.indexOf('<li', startIndex);
    if (liStart === -1) return null;

    const liOpenEnd = html.indexOf('>', liStart);
    if (liOpenEnd === -1) return null;

    const liEnd = this.findMatchingClosingTag(html, liOpenEnd + 1);
    if (liEnd === -1) return null;

    const liContent = html.substring(liOpenEnd + 1, liEnd);
    const { textContent, nestedContent } = this.separateTextAndNestedContent(liContent);

    const label = this.extractTextContent(textContent).trim();

    return {
      item: { label, nestedContent },
      nextIndex: liEnd + 5 // Move past </li>
    };
  }

  private separateTextAndNestedContent(content: string): { textContent: string; nestedContent?: string } {
    const nestedListStart = content.search(REGEX_PATTERNS.htmlNestedList);

    if (nestedListStart === -1) {
      return { textContent: content };
    }

    return {
      textContent: content.substring(0, nestedListStart),
      nestedContent: content.substring(nestedListStart)
    };
  }

  private findMatchingClosingTag(html: string, startIndex: number): number {
    let depth = 1;
    let searchIndex = startIndex;

    while (searchIndex < html.length && depth > 0) {
      const nextLiOpen = html.indexOf('<li', searchIndex);
      const nextLiClose = html.indexOf('</li>', searchIndex);

      if (nextLiClose === -1) break;

      if (nextLiOpen !== -1 && nextLiOpen < nextLiClose) {
        depth++;
        searchIndex = nextLiOpen + 3;
      } else {
        depth--;
        if (depth === 0) {
          return nextLiClose;
        }
        searchIndex = nextLiClose + 5;
      }
    }

    return -1;
  }

  private extractTextContent(html: string): string {
    // Remove HTML comments
    let text = html.replace(REGEX_PATTERNS.htmlComment, '');

    // Remove CDATA sections
    text = text.replace(REGEX_PATTERNS.cdataSection, '');

    // Remove HTML tags
    text = text.replace(REGEX_PATTERNS.htmlTag, '');

    // Normalize whitespace between removed elements
    text = text.replace(REGEX_PATTERNS.whitespaceNormalize, ' ');

    if (text.includes('&')) {
      Object.entries(HTML_ENTITIES).forEach(([entity, replacement]) => {
        text = text.replace(new RegExp(entity, 'g'), replacement);
      });
    }

    return text;
  }
}
